import { useCallback, useEffect, useRef, useState } from 'react'

interface SpeechRecognitionEvent extends Event {
	results: SpeechRecognitionResultList
	resultIndex: number
}

interface SpeechRecognitionResultList {
	length: number
	item(index: number): SpeechRecognitionResult
	[index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
	length: number
	item(index: number): SpeechRecognitionAlternative
	isFinal: boolean
	[index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
	transcript: string
	confidence: number
}

interface SpeechRecognitionInstance extends EventTarget {
	continuous: boolean
	interimResults: boolean
	lang: string
	onresult: ((event: SpeechRecognitionEvent) => void) | null
	onerror: ((event: Event & { error?: string }) => void) | null
	onend: (() => void) | null
	start(): void
	stop(): void
	abort(): void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance

export interface SpeechRecognitionOptions {
	autoSend?: boolean
	continuous?: boolean
}

export function useSpeechRecognition(
	onResult: (text: string) => void,
	onAutoSend?: (text: string) => void,
	options: SpeechRecognitionOptions = {},
) {
	const { autoSend = true, continuous = true } = options
	const [isListening, setIsListening] = useState(false)
	const [isSupported, setIsSupported] = useState(false)
	const [interimText, setInterimText] = useState('')
	const [error, setError] = useState<string | null>(null)
	const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
	const onResultRef = useRef(onResult)
	const onAutoSendRef = useRef(onAutoSend)
	const autoSendRef = useRef(autoSend)

	useEffect(() => { onResultRef.current = onResult }, [onResult])
	useEffect(() => { onAutoSendRef.current = onAutoSend }, [onAutoSend])
	useEffect(() => { autoSendRef.current = autoSend }, [autoSend])

	useEffect(() => {
		const SpeechRecognitionCtor: SpeechRecognitionCtor | undefined =
			(window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
		setIsSupported(!!SpeechRecognitionCtor)
		if (!SpeechRecognitionCtor) return

		const recognition = new SpeechRecognitionCtor()
		recognition.continuous = continuous
		recognition.interimResults = true
		recognition.lang = 'en-US'

		recognition.onresult = (event: SpeechRecognitionEvent) => {
			let interim = ''
			let finalTranscript = ''

			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i]
				const text = result[0]?.transcript ?? ''
				if (result.isFinal) {
					finalTranscript += text
				} else {
					interim += text
				}
			}

			setInterimText(interim)

			if (finalTranscript) {
				const trimmed = finalTranscript.trim()
				onResultRef.current(trimmed)
				if (autoSendRef.current && onAutoSendRef.current) {
					onAutoSendRef.current(trimmed)
				}
				setInterimText('')
			}
		}

		recognition.onerror = (event: Event & { error?: string }) => {
			const errorMsg = (event as any).error ?? 'unknown'
			if (errorMsg !== 'aborted') {
				setError(errorMsg === 'not-allowed'
					? 'Microphone access denied'
					: errorMsg === 'no-speech'
						? 'No speech detected'
						: `Voice error: ${errorMsg}`)
			}
			setIsListening(false)
		}

		recognition.onend = () => {
			setIsListening(false)
			setInterimText('')
		}

		recognitionRef.current = recognition

		return () => {
			recognition.abort()
		}
	}, [continuous])

	const toggleListening = useCallback(() => {
		const recognition = recognitionRef.current
		if (!recognition) return

		setError(null)
		if (isListening) {
			recognition.stop()
			setIsListening(false)
			setInterimText('')
		} else {
			recognition.start()
			setIsListening(true)
		}
	}, [isListening])

	const clearError = useCallback(() => setError(null), [])

	return { isListening, isSupported, interimText, error, toggleListening, clearError }
}
